import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Modal from 'react-modal'
import { Link } from 'react-router-dom'
import {
  ApiError,
  CommentAttachmentDto,
  CommentDetailDto,
  CommentReplyDto,
  FEEDBACK_ATTACHMENT_MAX_BYTES,
  FEEDBACK_ATTACHMENT_MAX_COUNT,
  FEEDBACK_ATTACHMENT_MIME_TYPES,
} from '@ivy-monorepo/shared'
import dayjs from 'dayjs'
import Avatar from './Avatar'
import Icon from './Icon'
import { adminService } from '../services/admin.service'
import { formatTicketPrice } from '../utils/ticketPrice'
import { REFUND_STATUS_LABELS } from '../utils/refundStatus'

const MAX_ATTACHMENT_MB = FEEDBACK_ATTACHMENT_MAX_BYTES / (1024 * 1024)

const FeedbackAttachments = ({ attachments }: { attachments?: CommentAttachmentDto[] }) => {
  if (!attachments?.length) return null
  return (
    <div className="feedback-attachments">
      {attachments.map(attachment => (
        <a key={attachment.publicId} href={attachment.url} target="_blank" rel="noreferrer">
          <img src={attachment.url} alt="Attachment" loading="lazy" />
        </a>
      ))}
    </div>
  )
}

interface FeedbackThreadModalProps {
  commentPublicId: string | null
  isOpen: boolean
  onClose: () => void
  onChanged?: () => void
  showUserProfileLink?: boolean
  /** Shows a back button (e.g. to the refund request the thread was opened from). */
  onBack?: () => void
  backLabel?: string
}

const FeedbackThreadModal = ({
  commentPublicId,
  isOpen,
  onClose,
  onChanged,
  showUserProfileLink = true,
  onBack,
  backLabel = 'Back',
}: FeedbackThreadModalProps) => {
  const [topic, setTopic] = useState<CommentDetailDto | null>(null)
  const [draft, setDraft] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const attachmentPreviews = useMemo(() => attachments.map(file => URL.createObjectURL(file)), [attachments])
  useEffect(() => () => attachmentPreviews.forEach(url => URL.revokeObjectURL(url)), [attachmentPreviews])

  const addAttachments = (files: File[]) => {
    const images = files.filter(file => FEEDBACK_ATTACHMENT_MIME_TYPES.includes(file.type))
    const withinSize = images.filter(file => file.size <= FEEDBACK_ATTACHMENT_MAX_BYTES)
    const room = FEEDBACK_ATTACHMENT_MAX_COUNT - attachments.length
    const accepted = withinSize.slice(0, Math.max(room, 0))

    if (images.length < files.length) {
      setAttachmentError('Only JPEG, PNG and WebP images can be attached.')
    } else if (withinSize.length < images.length) {
      setAttachmentError(`Each image must be ${MAX_ATTACHMENT_MB}MB or smaller.`)
    } else if (accepted.length < withinSize.length) {
      setAttachmentError(`You can attach up to ${FEEDBACK_ATTACHMENT_MAX_COUNT} images per message.`)
    } else {
      setAttachmentError(null)
    }
    if (accepted.length) setAttachments(current => [...current, ...accepted])
  }

  const removeAttachment = (index: number) => {
    setAttachments(current => current.filter((_, i) => i !== index))
    setAttachmentError(null)
  }

  const loadTopic = useCallback(async () => {
    if (!commentPublicId) return
    try {
      const data = await adminService.getFeedbackTopic(commentPublicId)
      setTopic(data)
    } catch (err) {
      console.log('Failed to load feedback topic', err)
    }
  }, [commentPublicId])

  useEffect(() => {
    if (isOpen && commentPublicId) {
      loadTopic()
    } else {
      setTopic(null)
      setDraft('')
      setAttachments([])
      setAttachmentError(null)
    }
  }, [isOpen, commentPublicId, loadTopic])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [topic?.replies.length])

  const handleSend = async () => {
    if (!commentPublicId || (!draft.trim() && attachments.length === 0) || isSending) return
    const content = draft.trim()
    setIsSending(true)
    try {
      const updated = await adminService.replyToFeedbackTopic(commentPublicId, content, attachments)
      setDraft('')
      setAttachments([])
      setAttachmentError(null)
      setTopic(updated)
      onChanged?.()
    } catch (err) {
      console.log('Failed to send feedback reply', err)
      const apiError = err as ApiError
      setAttachmentError(apiError.friendlyMessage || apiError.message || 'Failed to send reply.')
    } finally {
      setIsSending(false)
    }
  }

  const handleCloseTopic = async () => {
    if (!commentPublicId || isClosing || topic?.closedAt) return
    setIsClosing(true)
    try {
      const updated = await adminService.closeFeedbackTopic(commentPublicId)
      setTopic(updated)
      onChanged?.()
    } catch (err) {
      console.log('Failed to close feedback topic', err)
    } finally {
      setIsClosing(false)
    }
  }

  return (
    <Modal
      closeTimeoutMS={300}
      isOpen={isOpen}
      onRequestClose={onClose}
      className="conversation-detail-popup feedback-thread-popup"
      overlayClassName="conversation-detail-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
    >
      {topic ? (
        <div className="popup-detail-container">
          {onBack && (
            <button type="button" className="feedback-back-link" onClick={onBack}>
              <Icon name="ChevronLeft" />
              {backLabel}
            </button>
          )}
          <div className="feedback-thread-header">
            <div className="feedback-thread-title">
              <h2 title={topic.subject}>{topic.subject}</h2>
              <div className="feedback-thread-meta">
                <span>
                  {topic.account.firstName} {topic.account.lastName}
                </span>
                <span className={`feedback-status ${topic.closedAt ? 'is-closed' : 'is-open'}`}>
                  {topic.closedAt ? 'Closed' : 'Open'}
                </span>
                {topic.venueSuggestion ? (
                  <span className="feedback-status feedback-type-venue-suggestion">Venue suggestion</span>
                ) : null}
                {topic.refundRequest ? (
                  <span className="feedback-status feedback-type-refund">Refund request</span>
                ) : null}
              </div>
            </div>
            <div className="feedback-thread-actions">
              {showUserProfileLink && topic.userPublicId && (
                <Link to={`/admin/users/${topic.userPublicId}`} className="styled-button secondary feedback-icon-label">
                  <Icon name="ArrowUpRightFromSquare" />
                  Open user profile
                </Link>
              )}
              {!topic.closedAt && (
                <button
                  type="button"
                  className="styled-button secondary"
                  onClick={handleCloseTopic}
                  disabled={isClosing}
                >
                  {isClosing ? 'Closing…' : 'Close topic'}
                </button>
              )}
            </div>
          </div>

          <div className="popup-messages-section">
            {topic.venueSuggestion ? (
              <div className="feedback-venue-suggestion-card">
                <div className="feedback-original-label">Suggested venue</div>
                <div className="feedback-venue-suggestion-name">{topic.venueSuggestion.name}</div>
                <div className="feedback-venue-suggestion-meta">
                  {topic.venueSuggestion.latitude != null && topic.venueSuggestion.longitude != null ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${topic.venueSuggestion.latitude},${topic.venueSuggestion.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {topic.venueSuggestion.latitude.toFixed(5)}, {topic.venueSuggestion.longitude.toFixed(5)}
                    </a>
                  ) : null}
                  <span>Status: {topic.venueSuggestion.status}</span>
                </div>
              </div>
            ) : null}

            {topic.refundRequest ? (
              <div className="feedback-venue-suggestion-card feedback-refund-card">
                <div className="feedback-original-label">Refund request</div>
                <div className="feedback-venue-suggestion-name">
                  {formatTicketPrice(topic.refundRequest.amountCents)} · {topic.refundRequest.eventName}
                </div>
                <div className="feedback-venue-suggestion-meta">
                  <span>Status: {REFUND_STATUS_LABELS[topic.refundRequest.status]}</span>
                  <Link to="/admin/tickets?tab=refunds">Open in Tickets › Refunds</Link>
                </div>
              </div>
            ) : null}

            <div className="feedback-original-post">
              <div className="feedback-original-label">Original feedback</div>
              <div className="feedback-original-body">{topic.message}</div>
              <FeedbackAttachments attachments={topic.attachments} />
              <div className="feedback-original-time">{dayjs(topic.createdAt).format('MMM DD, YYYY [at] hh:mma')}</div>
            </div>

            <div className="feedback-messages">
              {topic.replies.length === 0 && <div className="feedback-messages-empty">No replies yet</div>}
              {topic.replies.map((reply: CommentReplyDto) => (
                <div key={reply.publicId} className={`feedback-message ${reply.isFromAdmin ? 'is-admin' : 'is-user'}`}>
                  <Avatar
                    url={reply.author.imageUrl ?? undefined}
                    firstName={reply.author.firstName}
                    lastName={reply.author.lastName}
                  />
                  <div className="feedback-message-body">
                    <div className="feedback-message-meta">
                      <span className="feedback-message-author">
                        {`${reply.author.firstName} ${reply.author.lastName}`.trim()}
                      </span>
                      <span>{dayjs(reply.createdAt).format('MMM DD, YYYY [at] hh:mma')}</span>
                    </div>
                    <div className="feedback-message-bubble">
                      {reply.content ? <div className="feedback-message-content">{reply.content}</div> : null}
                      <FeedbackAttachments attachments={reply.attachments} />
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="feedback-composer">
            {attachments.length > 0 && (
              <div className="feedback-attachment-drafts">
                {attachments.map((file, index) => (
                  <div key={attachmentPreviews[index]} className="feedback-attachment-draft">
                    <img src={attachmentPreviews[index]} alt={file.name} />
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeAttachment(index)}
                      disabled={isSending}
                    >
                      <Icon name="Xmark" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault()
                  handleSend()
                }
              }}
              onPaste={event => {
                const files = Array.from(event.clipboardData.files)
                if (files.length) {
                  event.preventDefault()
                  addAttachments(files)
                }
              }}
              placeholder="Write a reply… (paste a screenshot to attach it)"
              rows={3}
            />
            <div className="feedback-composer-footer">
              <input
                ref={fileInputRef}
                type="file"
                accept={FEEDBACK_ATTACHMENT_MIME_TYPES.join(',')}
                multiple
                hidden
                onChange={event => {
                  addAttachments(Array.from(event.target.files ?? []))
                  event.target.value = ''
                }}
              />
              <button
                type="button"
                className="feedback-attach-button"
                title={`Attach images (up to ${FEEDBACK_ATTACHMENT_MAX_COUNT}, ${MAX_ATTACHMENT_MB}MB each)`}
                aria-label="Attach images"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending || attachments.length >= FEEDBACK_ATTACHMENT_MAX_COUNT}
              >
                <Icon name="Paperclip" />
              </button>
              <span className="feedback-composer-hint">⌘/Ctrl + Enter to send</span>
              <button
                type="button"
                className="styled-button"
                onClick={handleSend}
                disabled={isSending || (!draft.trim() && attachments.length === 0)}
              >
                {isSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
          {attachmentError && <div className="feedback-attachment-error">{attachmentError}</div>}
        </div>
      ) : (
        <div />
      )}
    </Modal>
  )
}

export default FeedbackThreadModal
